import type { MutableRefObject } from 'react';
import React, { useRef, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import MenuItem from '@components/MenuItem/index';
import UploadRecordsContent from '@components/Modal/Content/UploadRecordsModalContent/UploadRecordsContent';

import { RECORD_UPDATE_TYPES } from '@constants/record-update-types';

import { useIsMobile } from '@hooks/useIsMobile';
import { useModal } from '@hooks/useModal';

import { SideDrawerStyle } from './SideDrawer.style';

const messages = defineMessages({
  report: {
    id: 'reportDrawer.report',
    defaultMessage: 'Report',
  },
  update: {
    id: 'reportDrawer.update',
    defaultMessage: 'Tell us about an encounter',
  },
  missing: {
    id: 'reportDrawer.missing',
    defaultMessage: 'Something missing?',
  },
  incorrect: {
    id: 'reportDrawer.incorrect',
    defaultMessage: 'Something not right?',
  },
  upload: {
    id: 'reportDrawer.upload',
    defaultMessage: 'Upload a record/document',
  },
});

function ReportDrawer({
  isOpen = false,
  onClose,
  btnRef = undefined,
}: {
  isOpen?: boolean;
  onClose?: (type?: RECORD_UPDATE_TYPES) => void;
  btnRef?: MutableRefObject<HTMLButtonElement | null>;
}) {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();

  const drawerRef = useRef<HTMLInputElement>(null);
  const mobile = useIsMobile();

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (
        isOpen &&
        onClose &&
        !drawerRef.current?.contains(event.target as Node) &&
        !btnRef?.current?.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClick, false);
    return () => document.removeEventListener('mousedown', handleClick, false);
  }, [onClose, isOpen, btnRef]);

  function selectItem(type: RECORD_UPDATE_TYPES | null) {
    if (onClose && type) {
      onClose(type);
    }
  }

  const handleOpenModal = () => {
    openModal({
      width: 500,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: <UploadRecordsContent onSubmit={closeModal} />,
    });
  };

  return (
    <SideDrawerStyle
      className={`${!isOpen && 'hidden'} ${mobile && 'mobile'}`}
      ref={drawerRef}
    >
      <h3>{formatMessage(messages.report)}</h3>

      <MenuItem
        icon={[{ iconName: 'files-medical' }]}
        onClick={() => selectItem(RECORD_UPDATE_TYPES.RECORD_UPDATE)}
        text={formatMessage(messages.update)}
      />

      <MenuItem
        icon={[{ iconName: 'comment-question' }]}
        onClick={() => selectItem(RECORD_UPDATE_TYPES.MISSING_INFORMATION)}
        text={formatMessage(messages.missing)}
      />

      <MenuItem
        icon={[{ iconName: 'circle-exclamation' }]}
        onClick={() => selectItem(RECORD_UPDATE_TYPES.INCORRECT_INFORMATION)}
        text={formatMessage(messages.incorrect)}
      />

      <MenuItem
        icon={[{ iconName: 'files' }]}
        onClick={handleOpenModal}
        text={formatMessage(messages.upload)}
      />
    </SideDrawerStyle>
  );
}

export default ReportDrawer;
